/**
 * Countries, seaports, airports and East African inland hubs for origin and
 * destination pickers. The list is built by scripts/build-location-reference.ts
 * from OurAirports, UN/LOCODE and the World Port Index, and served as a static
 * file so it loads once, only when a picker opens.
 *
 * No `@/` imports here: root tests import this module directly.
 */

export type LocationKind = 'COUNTRY' | 'SEAPORT' | 'AIRPORT' | 'INLAND'
export type ShipmentMode = 'AIR' | 'SEA' | 'ROAD' | 'NONE'
export type LocationRole = 'origin' | 'destination'

export interface Location {
  /** UN/LOCODE for ports and inland places (KEMBA), IATA for airports (EBB), ISO for countries (CN). */
  code: string
  kind: LocationKind
  name: string
  city: string
  /** ISO country code. */
  country: string
  countryName: string
  /** 1–3: bigger and busier places rank higher. */
  weight: number
  aliases: string
}

interface LocationFile {
  countries: Array<[string, string]>
  places: Array<[string, Exclude<LocationKind, 'COUNTRY'>, string, string, string, number, string?]>
}

export interface LocationIndex {
  all: Location[]
  byCode: Map<string, Location>
}

export function indexLocations(file: LocationFile): LocationIndex {
  const countryNames = new Map(file.countries)
  const all: Location[] = [
    ...file.countries.map(([code, name]): Location => ({
      code,
      kind: 'COUNTRY',
      name,
      city: '',
      country: code,
      countryName: name,
      weight: 2,
      aliases: '',
    })),
    ...file.places.map(([code, kind, name, city, country, weight, aliases]): Location => ({
      code,
      kind,
      name,
      city,
      country,
      countryName: countryNames.get(country) ?? country,
      weight,
      aliases: aliases ?? '',
    })),
  ]
  return { all, byCode: new Map(all.map((location) => [location.code, location])) }
}

let loading: Promise<LocationIndex> | null = null

/** Fetches the list once per page load. */
export function loadLocations(url = '/reference/locations.json'): Promise<LocationIndex> {
  loading ??= fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Locations failed to load (${response.status})`)
      return response.json() as Promise<LocationFile>
    })
    .then(indexLocations)
    .catch((error) => {
      loading = null
      throw error
    })
  return loading
}

/** Which kinds of place make sense for a field, given how the goods travel. */
export function kindsFor(role: LocationRole, mode: ShipmentMode): LocationKind[] {
  if (mode === 'AIR') return role === 'origin' ? ['AIRPORT', 'COUNTRY'] : ['AIRPORT']
  if (mode === 'SEA') return role === 'origin' ? ['SEAPORT', 'COUNTRY'] : ['SEAPORT', 'INLAND']
  if (mode === 'ROAD') return role === 'origin' ? ['INLAND', 'SEAPORT', 'COUNTRY'] : ['INLAND']
  return ['COUNTRY', 'SEAPORT', 'AIRPORT', 'INLAND']
}

/** Shown before anything is typed: where Xtellar's goods usually go. */
const COMMON: Record<LocationRole, Record<ShipmentMode, string[]>> = {
  origin: {
    AIR: ['CAN', 'PVG', 'SZX', 'DXB', 'BOM', 'IST'],
    SEA: ['CNSGH', 'CNNBO', 'CNYTN', 'CNQIN', 'AEJEA', 'INNSA'],
    ROAD: ['KEMBA', 'TZDAR', 'KENBO'],
    NONE: ['CN', 'AE', 'IN', 'TR', 'KE'],
  },
  destination: {
    AIR: ['EBB', 'NBO', 'DAR', 'KGL'],
    SEA: ['KEMBA', 'TZDAR', 'UGKLA'],
    ROAD: ['UGKLA', 'UGMAL', 'UGBUS', 'UGJIN'],
    NONE: ['UGKLA', 'EBB', 'KEMBA', 'TZDAR'],
  },
}

const fold = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

function score(location: Location, query: string): number {
  if (location.code.toLowerCase() === query) return 1000
  const name = fold(location.name)
  let best = 0
  if (name.startsWith(query)) best = 500
  else if (name.split(/[\s\-'(/,]+/).some((word) => word.startsWith(query))) best = 300
  else if (fold(location.city).startsWith(query) || fold(location.aliases).includes(query))
    best = 250
  else if (fold(location.countryName).startsWith(query)) best = 150
  else if (name.includes(query)) best = 100
  return best ? best + location.weight * 10 : 0
}

export function searchLocations(
  index: LocationIndex,
  query: string,
  options: { kinds: LocationKind[]; role: LocationRole; mode: ShipmentMode; limit?: number },
): Location[] {
  const limit = options.limit ?? 30
  const kinds = new Set(options.kinds)
  const folded = fold(query.trim())
  if (!folded) {
    return COMMON[options.role][options.mode]
      .map((code) => index.byCode.get(code))
      .filter((location): location is Location => Boolean(location && kinds.has(location.kind)))
  }
  const scored: Array<[number, Location]> = []
  for (const location of index.all) {
    if (!kinds.has(location.kind)) continue
    const value = score(location, folded)
    if (value) scored.push([value, location])
  }
  return scored
    .sort((a, b) => b[0] - a[0] || a[1].name.localeCompare(b[1].name))
    .slice(0, limit)
    .map(([, location]) => location)
}

const KIND_LABEL: Record<LocationKind, string> = {
  COUNTRY: 'Country',
  SEAPORT: 'Seaport',
  AIRPORT: 'Airport',
  INLAND: 'Inland',
}

export function kindLabel(kind: LocationKind) {
  return KIND_LABEL[kind]
}

/** "Mombasa, Kenya", "Entebbe International Airport (EBB), Uganda", "China". */
export function locationLabel(location: Location) {
  if (location.kind === 'COUNTRY') return location.name
  if (location.kind === 'AIRPORT')
    return `${location.name} (${location.code}), ${location.countryName}`
  return `${location.name}, ${location.countryName}`
}

/** A picked place, or something typed that isn't in the list. */
export interface LocationChoice {
  code: string | null
  label: string
  country: string | null
  kind: LocationKind | null
}

export function choiceFrom(location: Location): LocationChoice {
  return {
    code: location.code,
    label: locationLabel(location),
    country: location.country,
    kind: location.kind,
  }
}

export function typedChoice(label: string): LocationChoice {
  return { code: null, label: label.trim(), country: null, kind: null }
}

/** Incoterms® 2020. The four sea rules only fit sea and inland waterway freight. */
export const INCOTERMS = [
  { code: 'EXW', name: 'Ex Works', seaOnly: false },
  { code: 'FCA', name: 'Free Carrier', seaOnly: false },
  { code: 'CPT', name: 'Carriage Paid To', seaOnly: false },
  { code: 'CIP', name: 'Carriage and Insurance Paid To', seaOnly: false },
  { code: 'DAP', name: 'Delivered at Place', seaOnly: false },
  { code: 'DPU', name: 'Delivered at Place Unloaded', seaOnly: false },
  { code: 'DDP', name: 'Delivered Duty Paid', seaOnly: false },
  { code: 'FAS', name: 'Free Alongside Ship', seaOnly: true },
  { code: 'FOB', name: 'Free on Board', seaOnly: true },
  { code: 'CFR', name: 'Cost and Freight', seaOnly: true },
  { code: 'CIF', name: 'Cost, Insurance and Freight', seaOnly: true },
] as const

export type IncotermCode = (typeof INCOTERMS)[number]['code']

export function incotermFitsMode(code: string, mode: ShipmentMode) {
  const term = INCOTERMS.find((entry) => entry.code === code)
  return !term || !term.seaOnly || mode === 'SEA' || mode === 'NONE'
}

/** For air and road, the any-mode rule people usually mean instead of a sea rule. */
export function incotermForMode(code: string): string | null {
  return (
    ({ FOB: 'FCA', FAS: 'FCA', CFR: 'CPT', CIF: 'CIP' } as Record<string, string>)[code] ?? null
  )
}

/**
 * A place written as text (from a screenshot, an email, an old record) as a
 * choice: the listed place when the code or name matches, otherwise the text.
 */
export function resolveLocation(
  index: LocationIndex,
  text: string,
  role: LocationRole,
  mode: ShipmentMode,
): LocationChoice {
  const trimmed = text.trim()
  const byCode = index.byCode.get(trimmed.toUpperCase())
  if (byCode && kindsFor(role, mode).includes(byCode.kind)) return choiceFrom(byCode)
  const name = fold(trimmed.split(',')[0] ?? '')
  const [best] = searchLocations(index, name, { kinds: kindsFor(role, mode), role, mode, limit: 1 })
  if (best && (fold(best.name) === name || fold(best.city) === name)) return choiceFrom(best)
  return typedChoice(trimmed)
}

/** "FOB Shanghai" → "FOB"; text that names no rule is kept as written. */
export function incotermFromText(text: string) {
  const first =
    text
      .trim()
      .split(/[\s,]+/)[0]
      ?.toUpperCase() ?? ''
  return INCOTERMS.some((term) => term.code === first) ? first : text.trim()
}

/** Where most of Xtellar's goods come from and go to; changed per shipment. */
export const CHINA: LocationChoice = { code: 'CN', label: 'China', country: 'CN', kind: 'COUNTRY' }
export const KAMPALA: LocationChoice = {
  code: 'UGKLA',
  label: 'Kampala, Uganda',
  country: 'UG',
  kind: 'INLAND',
}
